import { useCallback, useState } from 'react';
import { collection, query, where, orderBy, limit, getDocs, doc, getDoc } from 'firebase/firestore';
import { db } from '../lib/firebase/config';
import { Solve } from '../types/solve';

interface LatestSolveWithUser extends Solve {
  user: {
    displayName: string;
    username: string;
    photoURL?: string;
  };
  cube?: {
    id: string;
    brand: string;
    model: string;
    nickname?: string;
    slug: string;
  };
}

export const useLatestSolves = () => {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<Error | null>(null);

  const getLatestSolves = useCallback(async (limitCount: number = 5): Promise<LatestSolveWithUser[]> => {
    setLoading(true);
    setError(null);

    try {
      // Query solves collection for latest solves
      const solvesRef = collection(db, 'solves');
      const q = query(
        solvesRef,
        orderBy('timestamp', 'desc'),
        limit(limitCount)
      );

      console.log('Fetching solves...');
      const solvesSnapshot = await getDocs(q);
      console.log(`Found ${solvesSnapshot.docs.length} solves`);
      
      const solves = solvesSnapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      } as Solve));

      // Fetch user and cube data for each solve
      const solvesWithDetails = await Promise.all(
        solves.map(async (solve) => {
          try {
            // Get user data directly using userId as document ID
            console.log(`Fetching user data for userId: ${solve.userId}`);
            const userDoc = await getDoc(doc(db, 'users', solve.userId));
            const userData = userDoc.data();
            if (!userData) {
              console.warn(`No user data found for userId: ${solve.userId}`);
            }

            // Get cube data if cubeId exists
            let cubeData = undefined;
            if (solve.cubeId) {
              console.log(`Fetching cube data for cubeId: ${solve.cubeId}`);
              const cubeRef = doc(db, 'userCubes', solve.userId, 'cubes', solve.cubeId);
              const cubeSnap = await getDoc(cubeRef);
              if (cubeSnap.exists()) {
                const cube = cubeSnap.data();
                // Get cube model data
                const modelRef = doc(db, 'cubeModels', cube.modelId);
                const modelSnap = await getDoc(modelRef);
                if (modelSnap.exists()) {
                  const model = modelSnap.data();
                  cubeData = {
                    id: solve.cubeId,
                    brand: model.brand,
                    model: model.model,
                    nickname: cube.nickname,
                    slug: model.slug || `${model.brand}-${model.model}`.toLowerCase().replace(/[^a-z0-9]+/g, '-')
                  };
                } else {
                  console.warn(`No model data found for modelId: ${cube.modelId}`);
                }
              } else {
                console.warn(`No cube data found for cubeId: ${solve.cubeId}`);
              }
            }

            return {
              ...solve,
              user: {
                displayName: userData?.displayNameOriginal || userData?.displayName || 'Anonymous',
                username: userData?.normalizedDisplayName || userData?.displayName?.toLowerCase().replace(/\s+/g, '') || 'anonymous',
                photoURL: userData?.photoURL
              },
              cube: cubeData
            };
          } catch (err) {
            console.error('Error processing solve:', err);
            console.error('Solve data:', solve);
            throw err;
          }
        })
      );

      setLoading(false);
      return solvesWithDetails;
    } catch (err) {
      console.error('Error in getLatestSolves:', err);
      const error = err as Error;
      setError(error);
      setLoading(false);
      return [];
    }
  }, []);

  return {
    getLatestSolves,
    loading,
    error
  };
}; 